import { existsSync, promises as fs } from 'node:fs';
import path from 'node:path';
import { consola } from 'consola';
import {
  type MonocerosConfig,
  proxyHostPort,
  readMonocerosConfig,
} from '../config/global.js';
import { readConfig } from '../config/io.js';
import {
  containerConfigPath,
  containerConfigsDir,
  containerDir,
  containerEnvPath,
  monocerosHome as defaultMonocerosHome,
  globalEnvPath,
  prettyPath,
} from '../config/paths.js';
import {
  readEnvFile,
  expandEnvRefs,
  mergeEnvLayers,
  interpolateServices,
  interpolateFeatureOptions,
  interpolateMcpEntries,
  formatMissingVarsError,
  ensureEnvGitignored,
  setEnvVarRef,
  resolveGitUserFields,
  GIT_IDENTITY_VAR,
} from '../config/env-file.js';
import {
  PROVIDER_LABEL,
  PROVIDER_VALUES,
  REGEX,
  isValidEmail,
} from '../config/schema.js';
import {
  buildStateFile,
  readStateFile,
  writeStateFile,
} from '../config/state.js';
import type { SolutionConfig } from '../config/schema.js';
import { solutionConfigToCreateOptions } from '../config/transform.js';
import { loadDescriptorCatalog } from '../catalog/load.js';
import { resolveMcpServers } from '../catalog/mcp.js';
import {
  agentsPresent,
  formatNoAgentError,
} from '../create/mcp-registration.js';
import {
  resolveRuntimeImage,
  runtimeSupportsAppRestart,
  runtimeSupportsBrowserBridge,
  runtimeSupportsHostKeyPinning,
  runtimeSupportsSshAttach,
  serviceDefersStart,
} from '../create/catalog.js';
import { hasWantedApps, runAppCtl } from '../devcontainer/app-control.js';
import { spawnBridgeDaemon } from '../devcontainer/bridge-daemon.js';
import {
  type KeygenSpawn,
  recordHostKey,
  setupSshAttach,
  windowsSshPort,
} from '../devcontainer/ssh-attach.js';
import {
  needsCompose,
  normalizeOptions,
  validateOptions,
  writeScaffold,
} from '../create/scaffold.js';
import { cyan, dim, red, sectionLine, stripAnsi } from '../util/format.js';
import { migrateDeprecatedFeatureRef } from '../util/ref.js';
import { createApplyLog, teeApplyLogger } from './apply-log.js';
import { migrateServiceDataVolumes } from './service-data-volumes.js';
import {
  type ApplyProgress,
  createApplyProgress,
  createSigintAbort,
  logFileOnlyLogger,
  progressTeeLogger,
} from './apply-progress.js';
import { buildApplySummary, formatApplySummary } from './apply-summary.js';
import {
  formatFeatureNotes,
  readFeatureNotes,
} from '../create/feature-notes.js';
import { writeBriefing } from '../briefing/index.js';
import { loadComponentCatalog } from '../init/components.js';
import {
  type ComposeSpawn,
  type DockerExec,
  pullServiceImages,
  runContainerCycle,
  startDeferredServices,
} from '../devcontainer/compose.js';
import { resolveContainerImageId } from '../devcontainer/images.js';
import {
  DEFAULT_UPGRADE_STALE_DAYS,
  readMachineState,
  recordBuiltImage,
  upgradeNudge,
} from '../config/machine-state.js';
import {
  collectGitCredentials,
  uniqueHttpsHosts,
  formatUnknownProviderError,
} from '../devcontainer/credentials.js';
import {
  formatPluginFailures,
  installPlugins,
  type PluginFailure,
  pluginCredentialHosts,
  resolvePluginSources,
} from './plugins.js';
import {
  type DockerInfoSpawn,
  detectDockerMode,
  formatRootlessNotSupportedError,
} from '../devcontainer/docker-mode.js';
import { type DevcontainerSpawn } from '../devcontainer/cli.js';
import { waitForDockerDaemon } from '../devcontainer/daemon-ready.js';
import {
  ensureProxy,
  defaultDockerExec,
  type DockerExec as ProxyDockerExec,
} from '../proxy/index.js';
import { removeDynamicConfig, writeDynamicConfig } from '../proxy/dynamic.js';
import { httpServices } from '../config/http-services.js';
import { preflightHostPort } from '../proxy/port-check.js';
import {
  collectGitIdentity,
  type IdentityPrompt,
  type IdentityScopePrompt,
  type IdentitySpawn,
} from '../devcontainer/identity.js';
import {
  type FailedCloneRepo,
  type FeatureTokenPrompt,
  formatFailedClones,
  formatUnauthenticatedRepos,
  formatTokenUse,
  resolveRepoTokens,
  tokenAdviceLines,
} from './repo-token.js';

/**
 * `monoceros apply <name>` — read the yml at
 * `<MONOCEROS_HOME>/container-configs/<name>.yml`, materialize the
 * devcontainer scaffold at `<MONOCEROS_HOME>/container/<name>/`, write
 * `.monoceros/state.json` with `origin: <name>`, then teardown + bring
 * the container up.
 *
 * The target location is determined by convention, not by cwd or an
 * explicit path argument. That's deliberate: a config is the source of
 * truth, the container directory mirrors it 1:1, and the builder never
 * has to remember "which directory was sandbox materialized into".
 *
 * Idempotent: re-running picks up the current yml, overwrites scaffold
 * files, restarts the container.
 *
 * Refuses to materialize into a non-empty directory whose state.json
 * points at a different origin — protects against accidental clobber
 * if a builder somehow seeded `<MONOCEROS_HOME>/container/<name>/`
 * outside of this command.
 */

export interface RunApplyOptions {
  /** Config name — resolves to `<home>/container-configs/<name>.yml`. */
  name: string;
  cliVersion: string;
  /** Override of the user-data home. Tests inject a tmpdir. */
  monocerosHome?: string;
  now?: Date;
  /**
   * Rebuild feature layers from scratch (`--build-no-cache`), so feature
   * tools re-pull their latest versions instead of reusing the frozen cached
   * layer. Set by `monoceros upgrade`; a routine `apply` leaves it false and
   * uses the cache. See ADR 0018.
   */
  rebuild?: boolean;
  /**
   * When true, stream the raw `@devcontainers/cli` output to stderr
   * exactly like before ADR 0013 step 2 — no spinner, no phase
   * detection, full transcript live. Also forced on when stderr is
   * not a TTY (CI, piped output). Defaults to false.
   */
  verbose?: boolean;
  /**
   * Override the stream the spinner writes to. Tests inject an
   * in-memory writable so the progress UI doesn't touch process.stderr.
   */
  progressOut?: NodeJS.WriteStream;
  logger?: {
    info: (msg: string) => void;
    success: (msg: string) => void;
    warn?: (msg: string) => void;
    /**
     * Print a structural section marker (`▸ Configuration` etc).
     * Optional — tests typically pass a silent logger without one,
     * in which case section markers are no-ops.
     */
    section?: (label: string) => void;
  };
  dockerExec?: DockerExec;
  devcontainerSpawn?: DevcontainerSpawn;
  /** `docker compose` spawn for the upgrade-time image re-pull. Tests inject. */
  composeSpawn?: ComposeSpawn;
  dockerInfoSpawn?: DockerInfoSpawn;
  identitySpawn?: IdentitySpawn;
  identityPrompt?: IdentityPrompt;
  identityScopePrompt?: IdentityScopePrompt;
  /**
   * Override the merged env used for repo-token resolution (ADR 0031).
   * Test seam: production reads `monoceros-config.env` + `<name>.env`.
   */
  env?: Record<string, string>;
  /**
   * Interactive pick for a provider CLI feature that has no repo but
   * several `GIT_TOKEN__<PROVIDER>_*` candidates (ADR 0031). Defaults to a
   * consola select; tests inject a deterministic pick.
   */
  featureTokenPrompt?: FeatureTokenPrompt;
  /** Override the docker exec used by the Traefik proxy lifecycle. */
  proxyDocker?: ProxyDockerExec;
  /** Override `ssh-keygen` for the SSH attach point (ADR 0022). Tests stub this. */
  sshKeygen?: KeygenSpawn;
  /** Override the user `.ssh` dir for SSH attach config (ADR 0022). Tests inject a tmpdir. */
  sshUserSshDir?: string;
}

export interface RunApplyResult {
  /** Absolute path to the materialized container directory. */
  targetDir: string;
  /** Absolute path to the source yml. */
  configPath: string;
  /** Exit code of the trailing `devcontainer up` step. */
  containerExitCode: number;
}

/** Sentinel for "leave the feature unauthenticated" in the select. */
const SKIP_FEATURE_TOKEN = '__skip__';

/**
 * Default {@link FeatureTokenPrompt}: a consola select over the org-keyed
 * candidates plus a "skip" option. Returns the chosen var, or null to
 * leave the feature unauthenticated (ADR 0031).
 */
const defaultFeatureTokenPrompt: FeatureTokenPrompt = async ({
  provider,
  host,
  candidates,
}) => {
  const choice = await consola.prompt(
    `The ${PROVIDER_LABEL[provider]} CLI feature has no repo to pick a token from. ` +
      `Which token should authenticate ${host}?`,
    {
      type: 'select',
      options: [
        ...candidates.map((v) => ({ label: v, value: v })),
        { label: 'Skip — leave unauthenticated', value: SKIP_FEATURE_TOKEN },
      ],
    },
  );
  return typeof choice === 'string' && choice !== SKIP_FEATURE_TOKEN
    ? choice
    : null;
};

export async function runApply(opts: RunApplyOptions): Promise<RunApplyResult> {
  const home = opts.monocerosHome ?? defaultMonocerosHome();
  const logger = opts.logger ?? {
    info: (msg) => consola.info(msg),
    success: (msg) => consola.success(msg),
    warn: (msg) => consola.warn(msg),
    // Default section renderer: empty line, bold-underlined "▸ Label",
    // empty line. Mirrors install.sh's section visuals.
    section: (label) => process.stderr.write(`\n${sectionLine(label)}\n\n`),
  };
  const section = (label: string) => logger.section?.(label);

  if (!REGEX.solutionName.test(opts.name)) {
    throw new Error(
      `Invalid config name: ${JSON.stringify(opts.name)}. Use letters, digits, '.', '_' or '-'.`,
    );
  }

  const ymlPath = containerConfigPath(opts.name, home);
  if (!existsSync(ymlPath)) {
    throw new Error(
      `No such config: ${ymlPath}. Run \`monoceros init <template> ${opts.name}\` first.`,
    );
  }

  const targetDir = containerDir(opts.name, home);
  await assertSafeTargetDir(targetDir, opts.name);

  // ── Configuration ────────────────────────────────────────────
  section('Configuration');

  const parsed = await readConfig(ymlPath);

  // The runtime image version must be pinned in the yml (ADR 0017). We
  // never silently adopt a default and re-image an existing container —
  // an unpinned yml is rejected with an actionable hint. `init` writes
  // the pin for new containers; pre-pinning ymls are pinned explicitly
  // or the container is recreated.
  if (!parsed.config.runtimeVersion) {
    throw new Error(
      `No runtime pinned for '${opts.name}': the yml has no 'runtimeVersion'. ` +
        `Pin it with \`monoceros upgrade ${opts.name} <version>\` (or add ` +
        `\`runtimeVersion: <version>\` to the yml), then re-apply.`,
    );
  }

  // Read global defaults early — feature option defaults from
  // `monoceros-config.yml` need to be merged before scaffold codegen,
  // and the git identity logic later in this function also needs the
  // global config.
  const globalConfig = await readMonocerosConfig({ monocerosHome: home });

  // Pre-M4 the canonical feature namespace was
  // `ghcr.io/monoceros/features/…`. After the M4 cut it moved to
  // `ghcr.io/getmonoceros/monoceros-features/…`. Old refs still
  // structurally parse as third-party refs, so apply would silently
  // try to pull them from GHCR and 404. Warn loudly and tell the
  // builder what to write instead — but don't rewrite their yml or
  // fail the apply, so they stay in control of the migration.
  warnOnDeprecatedFeatureRefs(parsed.config.features, globalConfig, logger);

  // Read the per-container env file (container-configs/<name>.env) — the
  // source for `${VAR}` references — BEFORE the transform, because
  // feature options must be resolved before they're merged with the
  // monoceros-config `defaults.features` cascade.
  const envPath = containerEnvPath(opts.name, home);
  await ensureEnvGitignored(containerConfigsDir(home));
  // Merge the global env (shared `${VAR}` values — repo PATs, ADR 0031)
  // UNDER the per-container env, so a container-level entry always wins.
  // `<name>.env` may reference the global pool (e.g.
  // `GITHUB_API_TOKEN=${GIT_TOKEN__GITHUB_KUNDE1}`, ADR 0031) — expand
  // those refs against the merged env so the container designates which
  // global token it uses without duplicating the secret.
  const envVars = expandEnvRefs(
    mergeEnvLayers(
      readEnvFile(globalEnvPath(home)),
      readEnvFile(envPath),
      opts.env ?? {},
    ),
  );

  // Repo token resolution (ADR 0031): resolve each declared repo's access
  // token by convention (feature var → GIT_TOKEN__<PROVIDER>_<SEGMENT> →
  // GIT_TOKEN__<PROVIDER>), inject it into the provider CLI feature, and
  // collect the per-host tokens for the git-credentials writer. No
  // prompting, no yml mutation. A repo with no token aborts the apply.
  // A missing token is NOT fatal: we can't tell a public repo (needs no
  // token) from a private one without a network probe, so we let the
  // in-container clone be the arbiter — public clones, private fails with
  // git's own error. Repos left without a token are surfaced in a
  // prominent warning at the END of apply (below), where it's actually
  // seen, naming the consequences and how to set a token.
  const catalog = await loadComponentCatalog();
  const repoTokens = resolveRepoTokens(parsed.config, catalog, envVars);

  // A provider CLI feature with no repo but several org tokens is
  // genuinely ambiguous (ADR 0031): only the builder knows which org this
  // container is for. Prompt once, inject the pick into the feature and
  // the git-credentials host map, then remember it as a `<name>.env`
  // reference (`<P>_API_TOKEN=${GIT_TOKEN__<P>_<ORG>}`) so the next apply
  // resolves it by convention without asking again.
  const featureTokenPrompt =
    opts.featureTokenPrompt ?? defaultFeatureTokenPrompt;
  for (const amb of repoTokens.ambiguous) {
    const chosen = await featureTokenPrompt(amb);
    if (!chosen) {
      // Skipped: surface it in the end-of-apply warning with the same
      // var hints as any other unauthenticated provider.
      const p = amb.provider.toUpperCase();
      repoTokens.missing.push({
        host: amb.host,
        provider: amb.provider,
        tried: [`${p}_API_TOKEN`, `GIT_TOKEN__${p}`],
      });
      continue;
    }
    const token = (envVars[chosen] ?? '').trim();
    repoTokens.hostTokens.set(amb.host, token);
    const feature = repoTokens.features.find((f) => f.ref === amb.featureRef);
    if (feature) feature.options = { ...feature.options, apiToken: token };
    // Remember the pick as a reference so the next apply resolves it by
    // convention without re-asking. The env is scaffolded with an empty
    // `<P>_API_TOKEN=` placeholder, so overwrite that line in place.
    const featureVar = `${amb.provider.toUpperCase()}_API_TOKEN`;
    const saved = await setEnvVarRef(
      envPath,
      opts.name,
      featureVar,
      `\${${chosen}}`,
    );
    logger.info(
      `Using ${chosen} for the ${PROVIDER_LABEL[amb.provider]} CLI` +
        (saved
          ? ` (saved as ${featureVar} in ${prettyPath(envPath)})`
          : ` (${featureVar} already set in ${prettyPath(envPath)})`),
    );
  }

  for (const use of repoTokens.used) {
    logger.info(formatTokenUse(use));
  }

  // Resolve `${VAR}` in FEATURE options first. A missing/empty value
  // becomes "" so the transform's merge skips it → the option falls
  // through to the global default (or stays unset). A filled value
  // overrides. This lets credential placeholders (`apiKey: ${VAR}`) be
  // active in the yml with a blank `.env` seed.
  const resolvedFeatures = interpolateFeatureOptions(
    repoTokens.features,
    envVars,
  );

  // Shape validation happened in readConfig; catalog validation
  // (which language/service exists) happens here against
  // create/scaffold's known set.
  const createOpts = normalizeOptions(
    solutionConfigToCreateOptions(
      { ...parsed.config, features: resolvedFeatures },
      globalConfig?.defaults?.features ?? {},
    ),
  );

  // Resolve `${VAR}` in SERVICE fields (post-transform — services don't
  // merge with defaults). Unlike features, an unresolved reference here
  // is a hard error: a silently-empty DB password fails far more
  // opaquely later.
  const interpServices = interpolateServices(createOpts.services, envVars);
  if (interpServices.missing.length > 0) {
    throw new Error(
      formatMissingVarsError(interpServices.missing, prettyPath(envPath)),
    );
  }
  createOpts.services = interpServices.services;
  // The workspace env spells out each reachable service's host-side address, so
  // it needs the port Traefik really binds (default 80, `routing.hostPort`
  // otherwise) - the same value the routes and the printed URLs use.
  createOpts.proxyHostPort = proxyHostPort(globalConfig);

  // Resolve the `mcpServers:` block: `${VAR}` from the env file first, then the
  // catalog lookup that turns a bare `name:` into a full server definition
  // (ADR 0045). An inline entry keeps its own definition and is not resolved.
  // Unresolved `${VAR}` in an inline definition is a hard error for the same
  // reason a service's is: a literal `${TOKEN}` in an argv or an
  // `Authorization` header registers a server that fails on first use.
  const interpMcp = interpolateMcpEntries(parsed.config.mcpServers, envVars);
  if (interpMcp.missing.length > 0) {
    throw new Error(
      formatMissingVarsError(interpMcp.missing, prettyPath(envPath)),
    );
  }
  const mcp = resolveMcpServers(
    interpMcp.entries,
    await loadDescriptorCatalog(),
  );
  for (const note of mcp.notes) (logger.warn ?? logger.info)(note);
  if (mcp.servers.length > 0) {
    if (agentsPresent(createOpts.features).length === 0) {
      throw new Error(formatNoAgentError(mcp.servers, opts.name));
    }
    createOpts.mcpServers = mcp.servers;
  }

  // Resolve `${VAR}` in git identities — the container-level `git.user`
  // and each repo's `git.user` — against the same env file. UNLIKE
  // services/features, a missing var is NOT an error here: the identity
  // falls through to the existing cascade (monoceros-config defaults →
  // host → prompt). Only a fully-resolved-but-malformed email is a hard
  // error, checked now that the actual value is known (the schema defers
  // email format to apply on purpose).
  //
  //   - container `git.user`: per field. A resolved field is used; an
  //     unresolved one is dropped so the cascade fills it (the cascade
  //     already resolves name/email independently).
  //   - repo `git.user`: all-or-nothing. A single missing var drops the
  //     whole per-repo override, so the repo inherits the container
  //     identity (.monoceros/gitconfig) — no Frankenstein name-from-env +
  //     email-from-cascade.
  const gitUserErrors: string[] = [];
  let containerGitOverride: { name?: string; email?: string } | undefined;
  if (parsed.config.git?.user) {
    const f = resolveGitUserFields(parsed.config.git.user, envVars);
    if (f.email.value !== undefined && !isValidEmail(f.email.value)) {
      gitUserErrors.push(
        `git.user.email resolved to "${f.email.value}", which is not a valid email`,
      );
    }
    const override = {
      ...(f.name.value !== undefined ? { name: f.name.value } : {}),
      ...(f.email.value !== undefined ? { email: f.email.value } : {}),
    };
    if (Object.keys(override).length > 0) containerGitOverride = override;
  }
  for (const repo of createOpts.repos ?? []) {
    if (!repo.gitUser) continue;
    const f = resolveGitUserFields(repo.gitUser, envVars);
    if (f.name.value === undefined || f.email.value === undefined) {
      // All-or-nothing: a field with no usable value (missing/empty var)
      // drops the whole per-repo override → the repo inherits the
      // container identity, which itself climbs the cascade.
      delete repo.gitUser;
      continue;
    }
    if (!isValidEmail(f.email.value)) {
      gitUserErrors.push(
        `repos[${repo.path}].git.user.email resolved to "${f.email.value}", which is not a valid email`,
      );
      continue;
    }
    repo.gitUser = { name: f.name.value, email: f.email.value };
  }
  if (gitUserErrors.length > 0) {
    throw new Error(
      `Invalid git identity after resolving ${prettyPath(envPath)}:\n` +
        gitUserErrors.map((e) => `  - ${e}`).join('\n') +
        `\n\nFix the value in the env file (or the yml).`,
    );
  }

  validateOptions(createOpts);
  logger.success(`yml validated ${dim(`(${prettyPath(ymlPath)})`)}`);

  // Refresh host git identity and HTTPS credentials before the
  // container teardown so they're in place when post-create.sh runs.
  // Identity resolution priority: yml override → monoceros-config.yml
  // defaults → host global → persisted .monoceros/gitconfig → prompt.
  //
  // Skip identity collection entirely when there's no obvious reason
  // to need one: no repos to clone, no resolved yml.git.user, no
  // defaults.git.user. Without those, asking the builder for a
  // committer identity is pure friction — they didn't ask for git
  // and might just want a sandbox container. They can `monoceros
  // add-repo` later, at which point the next apply re-evaluates and
  // collects identity then.
  //
  // A repo that already carries its own fully-resolved `git.user`
  // (still set after the resolve loop above) gets its committer
  // identity per-clone via the post-create script, independent of
  // this cascade. So it doesn't drive the container-identity prompt:
  // only a repo WITHOUT its own identity needs the container-wide
  // fallback. If every repo self-identifies, prompting for a
  // container identity is the same pure friction as the no-repos
  // case.
  //
  // The container `git.user` and monoceros-config `defaults.git.user`
  // drivers key off whether they RESOLVED to a value, not whether the
  // block textually exists. `init` always emits `git.user:
  // ${GIT_USER_NAME}/…` and the global-config generator always emits a
  // `defaults.git.user: { name: '', email: '' }` block, so mere presence
  // is meaningless — a block that resolves to nothing (blank env, empty
  // strings → undefined per GitUserSchema) is effectively absent. It must
  // not force a prompt on its own; only a genuinely resolved value (or a
  // repo needing the fallback) does.
  const reposNeedingContainerIdentity = (createOpts.repos ?? []).some(
    (repo) => !repo.gitUser,
  );
  const hasResolvedContainerGitUser = containerGitOverride !== undefined;
  const defaultGitUser = globalConfig?.defaults?.git?.user;
  const hasDefaultGitUser =
    defaultGitUser?.name !== undefined || defaultGitUser?.email !== undefined;
  const idLogger = {
    info: logger.info,
    warn: logger.warn ?? logger.info,
  };
  // Those three decide whether we may ASK, not whether we collect.
  // Collection is unconditional: post-create points the container's
  // `~/.gitconfig` at `.monoceros/gitconfig` for every container, so
  // skipping the write leaves a dangling include and a container whose
  // first commit dies with "Please tell me who you are". A workbench
  // with an AI agent and no repos used to land exactly there.
  //
  // What the conditions still gate is the interaction. Without one of
  // them we resolve in silence: the container override, the global
  // defaults, the host git config and a previously persisted value all
  // still apply, we just never open a prompt for a container that may
  // have no interest in git.
  //
  // The env is read straight from the merged layers, with no `${VAR}` in
  // any yml required. A name and an address are personal data, the env
  // file is where those belong, and tying them to a yml placeholder made
  // them inert for every workbench without repos - only
  // `init --with-repos` ever wrote that line.
  const envIdentity = {
    ...(envVars[GIT_IDENTITY_VAR.name]?.trim()
      ? { name: envVars[GIT_IDENTITY_VAR.name]!.trim() }
      : {}),
    ...(envVars[GIT_IDENTITY_VAR.email]?.trim()
      ? { email: envVars[GIT_IDENTITY_VAR.email]!.trim() }
      : {}),
  };
  const mayPromptForIdentity =
    reposNeedingContainerIdentity ||
    hasResolvedContainerGitUser ||
    hasDefaultGitUser;
  const silentPrompt = async () => undefined;
  const identity = await collectGitIdentity(targetDir, {
    ...(opts.identitySpawn ? { spawn: opts.identitySpawn } : {}),
    ...(Object.keys(envIdentity).length > 0 ? { env: envIdentity } : {}),
    ...(mayPromptForIdentity
      ? {
          ...(opts.identityPrompt ? { prompt: opts.identityPrompt } : {}),
          ...(opts.identityScopePrompt
            ? { scopePrompt: opts.identityScopePrompt }
            : {}),
        }
      : { prompt: silentPrompt, scopePrompt: silentPrompt }),
    ...(containerGitOverride
      ? { containerOverride: containerGitOverride }
      : {}),
    ...(globalConfig?.defaults?.git?.user
      ? { defaults: globalConfig.defaults.git.user }
      : {}),
    logger: idLogger,
  });

  // Persist a freshly-prompted identity to whichever scope the
  // builder picked. Scope `g` writes monoceros-config.yml's
  // `defaults.git.user`; `c` writes this container yml's
  // `git.user`; `b` does both. The `.monoceros/gitconfig` file
  // collectGitIdentity already wrote stays the in-container
  // mechanism — these writes are about making the value
  // recoverable on the next apply / next container without
  // re-prompting.
  if (identity.prompted) {
    await persistPromptedIdentity(identity.prompted, opts.name, home, logger);
  }
  // An identity that resolved to nothing is worth one line, because
  // everything downstream looks healthy: the container starts, git
  // works, and only the first commit fails. Name the one-time fix.
  if (identity.name === undefined || identity.email === undefined) {
    idLogger.warn(
      `No git identity for this container: a commit inside it will fail. Set ${GIT_IDENTITY_VAR.name} and ${GIT_IDENTITY_VAR.email} in ${prettyPath(globalEnvPath(home))} for every workbench, or in ${prettyPath(envPath)} for this one.`,
    );
  }
  // Pre-fetch HTTPS credentials for every unique host derived from
  // the declared repos. Pre-flight: if any host returns no credentials,
  // fail fast with provider-specific setup hints — much more
  // actionable than letting the in-container `git clone` later die
  // with "could not read Username".
  //
  // First pass: reject hosts whose provider couldn't be resolved
  // (non-canonical host without an explicit `provider:` in the yml).
  // Those produce a separate "set provider:" error message — much
  // more useful than a generic "no credentials" hint because the
  // builder might actually have credentials in their helper, but we
  // wouldn't know which CLI to suggest.
  // A plugin marketplace on a private host needs the same token as a private
  // repo: the agent CLI clones it in the container, through the same mounted
  // credential helper. So its host goes through the same provider resolution
  // and the same "declare `provider:`" gate as a repo, rather than failing
  // later inside the agent with a bare clone error.
  const hostsToFetch = uniqueHttpsHosts([
    ...(createOpts.repos ?? []),
    ...pluginCredentialHosts(parsed.config.features),
  ]);
  const unknownProviderHosts = hostsToFetch
    .filter((h) => h.provider === 'unknown')
    .map((h) => h.host);
  if (unknownProviderHosts.length > 0) {
    throw new Error(formatUnknownProviderError(unknownProviderHosts));
  }
  if (hostsToFetch.length > 0) {
    // Write the resolved per-host tokens (ADR 0031) into the credentials
    // file the in-container clone/push reads. Hosts without a token get no
    // line — not fatal (public repos clone read-only); the end-of-apply
    // warning covers the consequences.
    if (repoTokens.hostTokens.size > 0) {
      await collectGitCredentials(targetDir, hostsToFetch, {
        patByHost: repoTokens.hostTokens,
        logger: idLogger,
      });
    }
  }

  // Resolve declared agent plugins now, before anything is built: a
  // `plugins:` block on a feature whose agent cannot host plugins is a
  // config error, and finding that out after a five-minute image build
  // helps nobody. The install itself runs once the container is up.
  const pluginSources = resolvePluginSources(parsed.config.features, opts.name);

  // Who reports a missing token. A workbench with repos, or with a provider
  // CLI feature, has a git-access story of its own and gets the repo block.
  // One whose only https clone is a plugin marketplace does not: for it the
  // missing token IS the plugin failure, so the plugin block carries the fix
  // and the repo block stays away entirely.
  const hasRepos = (parsed.config.repos ?? []).length > 0;
  const providerCliRefs = PROVIDER_VALUES.map(
    (p) => catalog.get(p)?.file.contributes.features?.[0]?.ref,
  ).filter((ref): ref is string => ref !== undefined);
  const hasProviderCli = parsed.config.features.some((f) =>
    providerCliRefs.includes(f.ref),
  );
  const pluginsOwnTheTokenAdvice =
    pluginSources.length > 0 && !hasRepos && !hasProviderCli;

  // NOTE: repos are cloned IN the container (post-create.sh), using the
  // container's network + the mounted credential helper. We deliberately
  // do NOT probe or clone repos host-side: the host's network/credential
  // context isn't the container's (a host may fail to resolve a remote
  // the container reaches fine), so host-side gating produced spurious
  // pre-flight failures across platforms. The in-container clone is the
  // single source of truth and reports a real error if a repo genuinely
  // can't be reached. (The host-side clone added in ADR 0012 — for
  // service bind-mounts of repo files like init.sql — was reverted; that
  // ordering needs a container-side solution instead.)

  // ── Scaffold ─────────────────────────────────────────────────
  section('Scaffold');

  // Probe the host docker daemon. Two purposes today:
  //   - Refuse to apply on rootless Docker, which doesn't work with
  //     our bind-mount model (host/container file ownership doesn't
  //     line up; Docker doesn't expose the `idmap` mount option that
  //     would fix this). The refusal lands before any docker build
  //     or container start, so the builder gets a clear actionable
  //     message instead of permission-denied surprises mid-clone.
  //   - Plumb the mode through to scaffold for any future mode-
  //     dependent code paths (parameter is currently unused after
  //     the idmap revert — kept so the wiring is in place).
  // Absorb the Docker Desktop / WSL2 cold-start race before the first
  // docker call that matters: on a fresh Windows/WSL apply the daemon
  // can be up enough to answer `docker info` yet still fail the very
  // next `docker network create` / `docker ps` with an empty-stderr
  // `exit 1`, which is what sinks the proxy-network create and
  // devcontainer-cli's own `docker ps`. A bounded readiness wait smooths
  // that; a genuinely-down daemon still exhausts the budget and the real
  // error surfaces at the docker step below — same as before.
  const daemonReady = await waitForDockerDaemon({
    exec: opts.dockerExec ?? defaultDockerExec,
    onWait: () =>
      (logger.warn ?? logger.info)(
        'Waiting for the Docker daemon to become ready…',
      ),
  });
  if (!daemonReady) {
    (logger.warn ?? logger.info)(
      'Docker daemon still not responding — continuing; the next docker step will report the error.',
    );
  }

  const dockerMode = await detectDockerMode({
    ...(opts.dockerInfoSpawn ? { spawn: opts.dockerInfoSpawn } : {}),
  });
  if (dockerMode === 'rootless') {
    throw new Error(formatRootlessNotSupportedError());
  }

  await fs.mkdir(targetDir, { recursive: true });
  await writeScaffold(createOpts, targetDir, { dockerMode });

  // Service data moved from a host bind mount into a docker volume
  // (ADR 0036). A container created before that carries its database in
  // `data/<svc>/`; move that content into the volume once, before anything
  // starts, so the service comes up on its existing data instead of an
  // empty directory. No-op for a fresh container and on every later apply.
  await migrateServiceDataVolumes({
    name: opts.name,
    targetDir,
    services: createOpts.services,
    dockerExec: opts.dockerExec ?? defaultDockerExec,
    logger,
  });
  await writeStateFile(
    targetDir,
    buildStateFile({
      origin: opts.name,
      cliVersion: opts.cliVersion,
      runtimeImage: resolveRuntimeImage(createOpts.runtimeVersion),
      ...(opts.now ? { now: opts.now } : {}),
    }),
  );

  // SSH attach point (ADR 0022): mint a per-container keypair and
  // register a `Host monoceros-<name>` block so any SSH-capable IDE or a
  // plain `ssh monoceros-<name>` attaches to the running container with
  // zero config. Gated on the pinned runtime shipping sshd (>= 1.2.0);
  // older images have no sshd, so the config would point at a dead port.
  // Non-fatal: an ssh-keygen / config hiccup must not strand the apply.
  if (runtimeSupportsSshAttach(createOpts.runtimeVersion)) {
    try {
      const ssh = await setupSshAttach({
        name: opts.name,
        targetDir,
        home,
        // Direct-port Windows block only when the runtime publishes the port
        // (>= 1.3.5); otherwise the Windows block keeps the ProxyCommand.
        windowsDirectPort: runtimeSupportsHostKeyPinning(
          createOpts.runtimeVersion,
        )
          ? windowsSshPort(opts.name)
          : null,
        ...(opts.sshKeygen ? { keygen: opts.sshKeygen } : {}),
        ...(opts.sshUserSshDir ? { userSshDir: opts.sshUserSshDir } : {}),
        logger: idLogger,
      });
      if (ssh.configured) {
        logger.info(
          `SSH attach: \`ssh ${ssh.hostAlias}\` (or pick it in your IDE)`,
        );
      }
    } catch (err) {
      (logger.warn ?? logger.info)(
        `SSH attach setup skipped: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  // Briefing files for AI tools inside the container. Lives next to
  // the scaffold (AGENTS.md / CLAUDE.md at the workspace root,
  // .monoceros/commands.md). See ADR 0014. Failure here is logged but
  // does not abort apply — a missing briefing degrades AI-tool UX but
  // the container itself is functional.
  try {
    const components = await loadComponentCatalog();
    await writeBriefing({
      targetDir,
      createOpts,
      components,
      hostPort: proxyHostPort(globalConfig),
    });
  } catch (err) {
    const msg = `briefing files not written: ${err instanceof Error ? err.message : String(err)}`;
    (logger.warn ?? logger.info)(msg);
  }

  logger.success(`materialized into ${prettyPath(targetDir)}`);

  // Repos are cloned in-container by post-create.sh (see the NOTE above
  // the Scaffold section) — no host-side clone here.

  // ── Container ────────────────────────────────────────────────
  section('Container');

  // Open the per-apply log file under `<container>/logs/`. From this
  // point on, the wrapped `containerLogger` mirrors info/warn/success
  // into the log alongside the terminal, and `applyLog.sink` is teed
  // into the devcontainer-cli stream via `runContainerCycle`'s
  // `logSink` option. See ADR 0013.
  const applyLog = createApplyLog({
    name: opts.name,
    home,
    cliVersion: opts.cliVersion,
    configPath: ymlPath,
    ...(opts.now ? { now: opts.now } : {}),
  });

  // Decide between interactive (spinner) and verbose (raw stream)
  // mode. ADR 0013: spinner is default; `--verbose` and non-TTY
  // environments fall back to the live stream so CI logs and
  // builder-driven debugging stay intact.
  const progressOut = opts.progressOut ?? process.stderr;

  /**
   * The one way an end-of-apply warning block reaches the builder: on screen
   * framed by blank lines, and in the apply log with the colours stripped.
   * Every block goes through here (repo access, uncloned repos, feature
   * notes, agent plugins) so a fourth one cannot quietly pick its own
   * moment, indentation and colours. The block itself is built by a
   * `format*` function using `warnHeading`.
   */
  const writeWarningBlock = (block: string): void => {
    progressOut.write(`\n${block}\n`);
    applyLog.stream.write(`\n${stripAnsi(block)}\n`);
  };
  const interactive = (progressOut.isTTY ?? false) && !opts.verbose;
  const progress: ApplyProgress | null = interactive
    ? createApplyProgress({ out: progressOut, interactive: true })
    : null;

  // Loggers used inside the container section:
  //  - `containerLogger` carries status lines that must surface on
  //    screen (Features list, Traefik routing warning). In spinner
  //    mode these print above the spinner via println; in verbose
  //    mode they go through consola as before.
  //  - `internalLogger` is the chatter from compose pre-cleanup. In
  //    spinner mode it goes to the log file only — the spinner phase
  //    label already conveys "cleaning up". In verbose mode it goes
  //    to screen too.
  const containerLogger = progress
    ? progressTeeLogger(progress, applyLog.sink)
    : teeApplyLogger(logger, applyLog.sink);
  const internalLogger = progress
    ? logFileOnlyLogger(applyLog.sink)
    : containerLogger;

  // SIGINT cleanup. Without a handler, Ctrl+C leaves the spinner's
  // last frame stuck on screen (cursor mid-line, shell prompt glued
  // to it), the log file misses its last write-buffer chunk, and the
  // exit code is whatever Node defaults to. With the handler we stop
  // the spinner, write a final marker into the log, close it cleanly,
  // and exit with 130 (128 + SIGINT, conventional for Ctrl+C).
  //
  // The docker child is left to die from signal propagation — what
  // it leaves behind (half-created container, partial layer) is
  // cleaned up on the next `apply` via `--remove-existing-container`
  // / the compose pre-cleanup, so we do not try to undo it here.
  const onSigint = createSigintAbort({
    progress,
    out: progressOut,
    log: applyLog,
    formatLogPointer: (p) => dim(`log: ${prettyPath(p)}`),
    onExit: () => process.exit(130),
  });
  process.on('SIGINT', onSigint);

  let exitCode: number;
  // Collected during the container phase, reported at the end with the other
  // warning blocks rather than mid-spinner, where it would scroll away.
  let pluginFailures: PluginFailure[] = [];
  try {
    // First-apply context: devcontainer-cli prints "Error fetching image
    // details: No manifest found for …" for multi-arch GHCR images, then
    // sits silent for ~1 min while Docker pulls the runtime image.
    // Both are non-fatal — the buildx step right after consumes the
    // image fine. In spinner mode the phase label ("starting container…")
    // covers this, so the warning lives in the log file only. In verbose
    // mode it stays on screen as before.
    const pullWarning =
      'Pulling runtime image and building feature layers. First apply takes ~1–2 min (Docker downloads the multi-arch base); subsequent applies are cached and fast. devcontainer-cli may log a "No manifest found" line — harmless, the pull continues.';
    if (progress) {
      applyLog.stream.write(`# note: ${pullWarning}\n\n`);
    } else {
      containerLogger.info(dim(pullWarning));
    }

    // Bring up the shared Traefik singleton ahead of the devcontainer
    // when the yml declares ports, and refresh the dynamic config so
    // the routes match whatever the yml currently says. `ensureProxy`
    // and `writeDynamicConfig` are both idempotent; a second
    // devcontainer that also wants Traefik just joins the already-
    // running proxy. See ADR 0007.
    const ports = createOpts.ports ?? [];
    // Routing exists for two reasons now: a declared port on the workspace, and
    // a service the catalog marks reachable (`httpPort`). Either one needs the
    // singleton up, the host port free and a routes file; only when NEITHER is
    // left does the file go away again.
    const exposedServices = httpServices(createOpts.services);
    const hasRoutes = ports.length > 0 || exposedServices.length > 0;
    if (hasRoutes) {
      // Pre-flight: bail with an actionable hint before `docker run`
      // tries to bind a held port. Throws on conflict — the message
      // names the routing.hostPort escape hatch and asks the builder
      // to either free the port or set a different one.
      await preflightHostPort(proxyHostPort(globalConfig), {
        ...(opts.proxyDocker ? { docker: opts.proxyDocker } : {}),
      });
    }

    try {
      if (hasRoutes) {
        await writeDynamicConfig(opts.name, ports, {
          monocerosHome: home,
          services: exposedServices,
        });
        await ensureProxy({
          ...(opts.proxyDocker ? { docker: opts.proxyDocker } : {}),
          monocerosHome: home,
          hostPort: proxyHostPort(globalConfig),
          logger: containerLogger,
        });
      } else {
        // Neither a port nor an exposed service is left (or both were
        // removed since the last apply) — drop any stale dynamic-config
        // file, routes for services included. Filesystem only; the
        // proxy itself is offered for teardown by stop/remove, not
        // here (apply ends with the container up, not stopped).
        await removeDynamicConfig(opts.name, { monocerosHome: home });
      }
    } catch (err) {
      // Don't strand the apply if Traefik bookkeeping fails — surface
      // as a warn and keep going. The devcontainer itself is still
      // usable; the builder loses only the `<name>.localhost` routing,
      // which the next apply / `add-port` will retry.
      containerLogger.warn?.(
        `Could not sync Traefik routes: ${err instanceof Error ? err.message : String(err)}. The container will start, but \`<name>.localhost\` routing may not work until the next \`monoceros apply\`.`,
      );
    }

    // Seed the spinner phase before the actual work so the builder
    // sees an immediate hint of what is happening. The stream-driven
    // triggers in apply-progress.ts take over once devcontainer-cli
    // starts emitting recognizable lines.
    if (progress) {
      progress.setPhase(
        needsCompose(createOpts)
          ? 'cleaning up previous containers…'
          : 'starting container…',
      );
    }

    // `upgrade` re-pulls the service images before anything starts
    // (ADR 0052). Routine `apply` never does — it reuses the local cache,
    // like it reuses the base pin and the feature layers. Failures are
    // advisory: the cached image still starts, so a warn and carry on.
    if (opts.rebuild && needsCompose(createOpts)) {
      if (progress) progress.setPhase('re-pulling service images…');
      try {
        const pullExit = await pullServiceImages({
          root: targetDir,
          ...(opts.composeSpawn ? { spawn: opts.composeSpawn } : {}),
          logSink: applyLog.sink,
          silent: progress !== null,
          logger: internalLogger,
        });
        if (pullExit !== 0) {
          containerLogger.warn?.(
            `Could not re-pull every service image (exit ${pullExit}). The container starts from the cached images; see the apply log for which pull failed.`,
          );
        }
      } catch (err) {
        containerLogger.warn?.(
          `Could not re-pull service images: ${err instanceof Error ? err.message : String(err)}. The container starts from the cached images.`,
        );
      }
    }

    exitCode = await runContainerCycle(targetDir, {
      hasCompose: needsCompose(createOpts),
      ...(opts.rebuild ? { noCache: true } : {}),
      prewarmImage: resolveRuntimeImage(createOpts.runtimeVersion),
      ...(opts.dockerExec !== undefined ? { dockerExec: opts.dockerExec } : {}),
      ...(opts.devcontainerSpawn !== undefined
        ? { devcontainerSpawn: opts.devcontainerSpawn }
        : {}),
      logSink: applyLog.sink,
      ...(progress ? { progressSink: progress.streamSink, silent: true } : {}),
      logger: internalLogger,
    });

    // Second wave (ADR 0025): bring up services deferred out of the initial
    // `devcontainer up` now that post-create (the clone) has run — as a
    // spinner phase BEFORE "container ready", part of the same activity, not
    // a separate line after it. Compose output goes to the log (off-screen
    // in spinner mode). A failure is surfaced but does not flip the result.
    if (exitCode === 0) {
      const deferred = createOpts.services
        .filter((s) => serviceDefersStart(s.name))
        .map((s) => s.name);
      if (deferred.length > 0) {
        if (progress) progress.setPhase('starting services…');
        try {
          const deferExit = await startDeferredServices({
            root: targetDir,
            services: deferred,
            logSink: applyLog.sink,
            silent: progress !== null,
            logger: internalLogger,
          });
          if (deferExit !== 0) {
            containerLogger.warn?.(
              `Deferred service(s) ${deferred.join(', ')} did not start cleanly (exit ${deferExit}). The workspace is up; bring them up with \`monoceros start ${opts.name}\`.`,
            );
          }
        } catch (err) {
          containerLogger.warn?.(
            `Could not start deferred service(s) ${deferred.join(', ')}: ${err instanceof Error ? err.message : String(err)}. The workspace is up.`,
          );
        }
      }
    }

    // Agent plugins: register each declared marketplace and install the
    // plugins named on it, through the agent's own CLI inside the container
    // (ADR 0053). After the deferred services, because a plugin needs nothing
    // from them and the network call belongs at the end. Best-effort: the
    // workbench is up and sound, so a marketplace that cannot be reached is a
    // warning, not a failed apply.
    if (exitCode === 0 && pluginSources.length > 0) {
      if (progress) progress.setPhase('installing agent plugins…');
      try {
        const { installed, failures } = await installPlugins({
          root: targetDir,
          sources: pluginSources,
          logSink: applyLog.sink,
          silent: progress !== null,
          ...(opts.devcontainerSpawn !== undefined
            ? { spawn: opts.devcontainerSpawn }
            : {}),
        });
        if (installed.length > 0) {
          containerLogger.info(
            `Agent plugins installed: ${installed.join(', ')}.`,
          );
        }
        pluginFailures = failures;
      } catch (err) {
        pluginFailures = [
          {
            what: 'could not run the agent plugin install',
            cause: err instanceof Error ? err.message : String(err),
          },
        ];
      }
    }

    // Stop the spinner and surface the outcome. In spinner mode the
    // failure path also prints the captured tail (~15 last lines of the
    // devcontainer-cli stream) so the builder sees the actual error
    // without paging through the log file. In verbose mode the stream
    // was already on screen, so there is nothing to replay.
    if (progress) {
      if (exitCode === 0) {
        progress.succeed();
      } else {
        const { tailLines } = progress.fail();
        progressOut.write(`\n${red(`✘ apply failed (exit ${exitCode})`)}\n\n`);
        for (const line of tailLines) {
          progressOut.write(`  ${line}\n`);
        }
        if (tailLines.length > 0) progressOut.write('\n');
      }
    }

    // Inventory block on success: shows the builder what their yml just
    // materialized (features, services, languages, repos, ports, apt
    // packages, install URLs). Replaces the cherry-picked "Features: …"
    // line that used to print above the spinner. Mirrored into the log
    // file with ANSI escapes stripped so `cat …apply-….log` stays
    // readable.
    if (exitCode === 0) {
      // Start the host-side browser-bridge daemon so a tool in ANY container
      // session (an IDE/desktop-app SSH attach, not just `monoceros run`) can
      // open the host browser. Gated on the runtime shipping the always-on
      // relay (>= 1.3.3); idempotent + best-effort (never blocks apply).
      if (runtimeSupportsBrowserBridge(createOpts.runtimeVersion)) {
        spawnBridgeDaemon(targetDir);
      }

      // Record the container's (now stable) SSH host key in ~/.ssh/known_hosts
      // so the Claude desktop app's ssh2 (no trust-on-first-use) accepts it -
      // under the alias on macOS/Linux, under [127.0.0.1]:<port> on Windows.
      // The host key is persisted by sshd-up.sh (>= 1.3.5). Best-effort.
      if (runtimeSupportsHostKeyPinning(createOpts.runtimeVersion)) {
        try {
          await recordHostKey({
            name: opts.name,
            targetDir,
            ...(opts.sshUserSshDir ? { userSshDir: opts.sshUserSshDir } : {}),
          });
        } catch (err) {
          (logger.warn ?? logger.info)(
            `Recording the SSH host key skipped: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }

      // Restore apps that were running before this apply. The container was
      // recreated, so every long-running server the builder had up was torn
      // down with it. `monoceros-ctl reconcile` brings back exactly the set
      // that was "wanted" (a present-but-now-dead pid file under
      // `.monoceros/run/`, which survives the recreate on the bind mount),
      // skipping anything an explicit `monoceros stop` cleared - the same
      // `unless-stopped` contract the container group already follows (ADR
      // 0026 / 0028). Gated on the runtime shipping `reconcile`; the host-side
      // `hasWantedApps` probe keeps it (and its output) out of the common case
      // where nothing was running. Best-effort: never fails the apply. Output
      // (▸ app / ✓ target) streams straight through.
      if (
        runtimeSupportsAppRestart(createOpts.runtimeVersion) &&
        (await hasWantedApps(opts.name, home))
      ) {
        progressOut.write(`\n  ${dim('restoring apps that were running…')}\n`);
        try {
          await runAppCtl(opts.name, ['reconcile']);
        } catch (err) {
          (logger.warn ?? logger.info)(
            `Restoring running apps skipped: ${err instanceof Error ? err.message : String(err)}. Bring them back with \`monoceros start ${opts.name} <app>\`.`,
          );
        }
      }

      const summaryLines = buildApplySummary(createOpts);
      if (summaryLines.length > 0) {
        const formatted = formatApplySummary(summaryLines);
        progressOut.write(`\n${formatted}\n`);
        applyLog.stream.write(`\n${stripAnsi(formatted)}\n`);
      }

      // Notes a feature left during install: a version it could not install, a
      // credential it did not get. Written on disk rather than logged, because
      // the build log scrolls past behind the spinner and a cached rebuild
      // produces none of it while the condition still holds. Rendered in the
      // same warning vocabulary as the repo-access block: nothing is broken,
      // something wants the builder's attention.
      const notes = await readFeatureNotes(targetDir);
      if (notes.length > 0) {
        writeWarningBlock(formatFeatureNotes(notes));
      }

      // Faithful reporting: the summary lists declared repos, but the clone
      // runs in-container (post-create) and soft-fails, so a declared repo
      // may not actually be checked out. The workspace (incl. projects/) is
      // bind-mounted, and a failed clone rm -rf's its partial checkout, so a
      // missing projects/<path> is ground truth. Warn about those — sibling
      // to the missing-token warning below, at a later moment. Repos already
      // flagged there (no token at all) are skipped so nothing is
      // double-warned; what remains is "token present but clone still failed"
      // (e.g. a token without the required scopes). Best-effort: a stat error
      // other than "absent" leaves the repo unflagged (no false alarm).
      const missingTokenHosts = new Set(repoTokens.missing.map((m) => m.host));
      const failedClones: FailedCloneRepo[] = [];
      for (const repo of createOpts.repos ?? []) {
        if (!repo.path) continue;
        let host: string | undefined;
        try {
          host = new URL(repo.url).hostname;
        } catch {
          host = undefined;
        }
        if (host && missingTokenHosts.has(host)) continue;
        try {
          const st = await fs.stat(path.join(targetDir, 'projects', repo.path));
          if (!st.isDirectory()) {
            failedClones.push({ path: repo.path, url: repo.url });
          }
        } catch (err) {
          if ((err as NodeJS.ErrnoException).code === 'ENOENT') {
            failedClones.push({ path: repo.path, url: repo.url });
          }
        }
      }
      if (failedClones.length > 0) {
        writeWarningBlock(formatFailedClones(failedClones, opts.name));
      }

      // Sibling of the block above: declared in the yml, not in the container.
      if (pluginFailures.length > 0) {
        // A workbench that declares a marketplace and no repos gets ONE
        // warning, about the thing it declared. The missing token is the
        // cause of the plugin failure, so the fix belongs in this block
        // rather than in a second one from the repo world (see
        // `pluginsOwnTheTokenAdvice` below).
        writeWarningBlock(
          formatPluginFailures(
            pluginsOwnTheTokenAdvice
              ? pluginFailures.map(({ hint: _hint, ...rest }) => rest)
              : pluginFailures,
            opts.name,
            pluginsOwnTheTokenAdvice
              ? { tokenAdvice: tokenAdviceLines(repoTokens.missing, opts.name) }
              : {},
          ),
        );
      }

      // Record the image this apply built, so the upgrade prune can later
      // remove stale builds of this container — and never touch any image
      // Monoceros didn't record (ADR 0018). Best-effort: a docker hiccup here
      // must not fail an otherwise-successful apply.
      const now = opts.now ?? new Date();
      try {
        const imageId = await resolveContainerImageId(
          targetDir,
          opts.dockerExec ?? defaultDockerExec,
        );
        if (imageId) {
          await recordBuiltImage(
            { imageId, container: opts.name, builtAt: now.toISOString() },
            home,
          );
        }
      } catch {
        // ignore — recording is an optimization, not a correctness step
      }

      // Staleness nudge: when the last `monoceros upgrade` is older than the
      // threshold, remind the builder (non-blocking). Silent if no upgrade has
      // ever run — a freshly-built container is current by definition.
      const nudge = upgradeNudge(
        await readMachineState(home),
        now,
        globalConfig?.upgrade?.staleDays ?? DEFAULT_UPGRADE_STALE_DAYS,
      );
      if (nudge) {
        progressOut.write(`\n  ${dim(nudge)}\n`);
      }
    }

    // Repos left without a token (ADR 0031): a prominent, consequence-
    // spelling warning at the very end where it's actually seen — on both
    // the success path (before Next steps) and the failure path (after the
    // error tail, since a private-repo clone that failed for lack of a
    // token is exactly when this matters most).
    if (repoTokens.missing.length > 0) {
      if (!pluginsOwnTheTokenAdvice) {
        writeWarningBlock(
          formatUnauthenticatedRepos(repoTokens.missing, opts.name, {
            repos: hasRepos,
            plugins: pluginSources.length > 0,
            providerCli: hasProviderCli,
          }),
        );
      }
    }

    // Close the log before announcing its path — guarantees the file
    // is fully flushed to disk by the time the builder follows the
    // pointer. The path is printed on both the success and failure
    // path; on failure it is the breadcrumb pointing at the full
    // diagnostic above what fits in the tail.
    //
    // Direct write rather than `logger.info` — the default consola
    // logger prefixes info lines with a timestamp, which collides with
    // the structured look of the section. The leading blank line
    // separates the pointer from the summary block above.
    await applyLog.close();
    progressOut.write(`\n  ${dim(`log: ${prettyPath(applyLog.path)}`)}\n`);

    // ── Next steps ───────────────────────────────────────────────
    // Only print the wrap-up on a successful container start;
    // otherwise the failing devcontainer-cli output is the relevant
    // signal and a cheery "shell into it!" line would be misleading.
    if (exitCode === 0) {
      section('Next steps');
      logger.info(`  ${cyan(`monoceros shell ${opts.name}`)}`);
    }
  } finally {
    process.off('SIGINT', onSigint);
  }

  return { targetDir, configPath: ymlPath, containerExitCode: exitCode };
}

/**
 * `<MONOCEROS_HOME>/container/<name>/` is safe to (re-)materialize iff:
 *   - it doesn't exist or is empty (fresh apply), OR
 *   - it already carries `.monoceros/state.json` with the same origin
 *     (re-apply against the same yml), OR
 *   - the only top-level entry is `.monoceros/` and there's no
 *     state.json — that's a partial-apply remnant: pre-flight wrote
 *     `gitconfig` / `git-credentials` into `.monoceros/` before
 *     something (reachability failure, Ctrl-C, expired token, …)
 *     aborted the apply ahead of `writeStateFile`. We own
 *     `.monoceros/`, so re-running is safe.
 *
 * Anything else — state.json with a different origin, or files
 * outside `.monoceros/` that aren't ours — stays an error so we
 * don't clobber unrelated work.
 */
async function assertSafeTargetDir(
  targetDir: string,
  expectedOrigin: string,
): Promise<void> {
  if (!existsSync(targetDir)) return;
  const entries = await fs.readdir(targetDir);
  if (entries.length === 0) return;

  const state = await readStateFile(targetDir);
  if (state) {
    if (state.origin !== expectedOrigin) {
      throw new Error(
        `${targetDir} is already materialized from config '${state.origin}', not '${expectedOrigin}'. Delete the directory to re-target, or run \`monoceros apply ${state.origin}\`.`,
      );
    }
    return; // safe: re-apply same origin
  }

  // No state.json. If the only top-level entry is `.monoceros/`, this
  // is a partial-apply remnant from a failed earlier run — pre-flight
  // writes `.monoceros/gitconfig` and `.monoceros/git-credentials`
  // BEFORE the scaffold + state.json sequence, so a mid-apply abort
  // leaves exactly this shape behind. Treat it as recoverable.
  if (entries.length === 1 && entries[0] === '.monoceros') {
    return;
  }

  throw new Error(
    `Refusing to materialize into non-empty directory ${targetDir} (no Monoceros state.json found, and the directory has files we don't recognise). Delete the directory before re-running.`,
  );
}

interface MigrationLogger {
  warn?: (msg: string) => void;
  info: (msg: string) => void;
}

function warnOnDeprecatedFeatureRefs(
  containerFeatures: SolutionConfig['features'],
  globalConfig: MonocerosConfig | undefined,
  logger: MigrationLogger,
): void {
  const warn = logger.warn ?? logger.info;
  const seen = new Set<string>();
  const emit = (oldRef: string, source: string) => {
    if (seen.has(oldRef)) return;
    seen.add(oldRef);
    const newRef = migrateDeprecatedFeatureRef(oldRef);
    if (!newRef) return;
    warn(
      `Deprecated feature ref in ${source}: '${oldRef}'. ` +
        `Replace with '${newRef}' — the old namespace is no longer published. ` +
        `See docs/MIGRATION-M4.md for a sed snippet.`,
    );
  };

  for (const entry of containerFeatures) {
    emit(entry.ref, 'container yml');
  }
  const globalDefaults = globalConfig?.defaults?.features;
  if (globalDefaults) {
    for (const ref of Object.keys(globalDefaults)) {
      emit(ref, 'monoceros-config.yml');
    }
  }
}

/**
 * Persist an identity that came from the interactive prompt, into the
 * env: `GIT_USER_NAME` / `GIT_USER_EMAIL` in the global
 * `monoceros-config.env` (`g`), the container's own `<name>.env` (`c`),
 * or both.
 *
 * The env and not the yml, for two reasons. A name and an address are
 * personal data and the env files are the gitignored half, while the
 * yml is the part a builder shares. And `.monoceros/gitconfig` is no
 * longer read back as a source, so an answer that is not written to a
 * declared place would be gone on the next apply - the prompt has to
 * land somewhere real.
 *
 * `setEnvVarRef` never clobbers a value the builder already put there;
 * a non-empty key stays as it is and we say so.
 */
async function persistPromptedIdentity(
  prompted: { name: string; email: string; scope: 'g' | 'c' | 'b' },
  containerName: string,
  home: string,
  logger: {
    info: (msg: string) => void;
    warn?: (msg: string) => void;
  },
): Promise<void> {
  const targets: Array<{ path: string; label: string }> = [];
  if (prompted.scope === 'g' || prompted.scope === 'b') {
    targets.push({ path: globalEnvPath(home), label: 'every workbench' });
  }
  if (prompted.scope === 'c' || prompted.scope === 'b') {
    targets.push({
      path: containerEnvPath(containerName, home),
      label: `${containerName} only`,
    });
  }

  for (const target of targets) {
    try {
      const wroteName = await setEnvVarRef(
        target.path,
        containerName,
        GIT_IDENTITY_VAR.name,
        prompted.name,
      );
      const wroteEmail = await setEnvVarRef(
        target.path,
        containerName,
        GIT_IDENTITY_VAR.email,
        prompted.email,
      );
      if (wroteName || wroteEmail) {
        logger.info(
          `Saved the identity for ${target.label} in ${prettyPath(target.path)}.`,
        );
      } else {
        logger.warn?.(
          `${prettyPath(target.path)} already has ${GIT_IDENTITY_VAR.name} / ${GIT_IDENTITY_VAR.email} set - left them alone.`,
        );
      }
    } catch (err) {
      logger.warn?.(
        `Could not save the identity in ${prettyPath(target.path)}: ${err instanceof Error ? err.message : String(err)}. It applies to this container now, but you will be asked again on the next apply.`,
      );
    }
  }
}
